
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Main Alert
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       9th July 2021

*******************************************************************************************/
import React                    from 'react';
import { 
    styled, 
    Alert as AlertMUI,
    Box,
    Stack
}                               from '@mui/material';
import { useTranslation }       from 'contexts/TranslationContext';
import { CancelIconButton }     from 'components/iconButtons'

const Alert = styled(AlertMUI)({
    borderRadius : 0
})

const noop  = () => {}
const obj   = {}

/**
 * Full-Width Alert box with optional dismiss functionality.
 * 
 * This component displays an alert box with customizable properties. It can show a dismissable alert with a cancel button, 
 * and execute specific actions when the alert is opened or closed.
 * 
 * @component
 * @example
 * // Example usage
 * <MainAlert open={true} dismissable={true} onClose={handleClose} />
 * 
 * @param {object} props The props for the MainAlert component.
 * @param {boolean} [props.open=true] - Controls whether the alert is visible or not.
 * @param {function} [props.onOpen=noop] - Callback function to handle when the alert is opened.
 * @param {function} [props.onClose=noop] - Callback function to handle when the alert is closed.
 * @param {boolean} [props.dismissable=false] - If true, includes a dismiss button to close the alert.
 * @param {object} [props.CancelIconButtonProps={}] - Props to customize the `CancelIconButton`. 
 * @param {React.ReactNode} [props.action] - Custom action content for the alert (e.g., buttons or links).
 * 
 * @returns {JSX.Element|null} The rendered alert component, or null if not visible.
 */
export const MainAlert = React.forwardRef( (
    {
        open        : openIn        = true, 
        onOpen      : handleOpen    = noop, 
        onClose     : handleClose   = noop, 
        dismissable                 = false, 
        CancelIconButtonProps       = obj, 
        ...props
    }, 
    ref
) => {
    
    // Hook for translation (localization) support
    const [t] = useTranslation();

    // State to manage the visibility of the alert
    const [open, setOpen] = React.useState(openIn);

    // Reference to track the first render
    const isFirstRender = React.useRef(true);

    // Memoize CancelIconButtonProps to prevent unnecessary re-renders
    const memoizedCancelIconButtonProps = React.useMemo(() => CancelIconButtonProps, [CancelIconButtonProps]);

    // Handler for the cancel button click that will dismiss the alert
    const handleCancelClick = React.useCallback((event) => {

        // Call the onClick passed via CancelIconButtonProps (if any)
        memoizedCancelIconButtonProps?.onClick?.(event);

        // Set alert visibility to false (dismiss alert)
        setOpen(false);

    }, [memoizedCancelIconButtonProps]);

    // Effect to synchronize the `open` prop with the component's state
    React.useEffect(() => setOpen(openIn), [ openIn ]);

    // Effect to call `onOpen` or `onClose` when the alert visibility changes
    React.useEffect(() => {

        // Skip the effect on the first render
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Trigger onOpen or onClose callback based on the visibility of the alert
        open 
            ? handleOpen(open) 
            : handleClose(open);

    }, [handleClose, handleOpen, open]);

    // Return null to render nothing if the alert is not open
    if (!open)
        return null;

    return (
        <Box ref={ref} width="100%">
            <Alert 

                {...props}

                // Custom action content with dismiss button if `dismissable` is true
                action = {
                    dismissable 
                        ?   
                            (
                                // Stack to display custom actions and the cancel button
                                <Stack direction="row" alignItems="center" spacing={0.5}>

                                    {/* Custom action passed as prop */}
                                    { props?.action }

                                    {/* Dismiss button */}
                                    <CancelIconButton 
                                        size            = "small" 
                                        {...memoizedCancelIconButtonProps}
                                        onClick         = { handleCancelClick } 
                                        TooltipProps    = {{
                                            title : t('common.dismiss'),
                                            ...memoizedCancelIconButtonProps?.TooltipProps
                                        }}
                                    /> 

                                </Stack>
                            )
                        : props?.action
                } 
            />
        </Box>
    );
});

export default MainAlert;